import { Quote } from '@/types';
import QuoteLoading from './QuoteLoading';
import QuoteError from './QuoteError';
import QuoteNoContent from './QuoteNoContent';
import QuoteContent from './QuoteContent';

/**
 * Props for the QuoteDisplay component
 */
interface QuoteDisplayProps {
  /** The quote to display, null when no quote is available */
  quote: Quote | null;
  /** Whether a quote is currently being fetched */
  isLoading: boolean;
  /** Error message when quote fetching fails */
  error: string | null;
}

/**
 * Main quote display component that renders quotes in a terminal-style interface
 * 
 * Handles different states:
 * - Loading: Shows loading indicator
 * - Error: Shows error message or fallback quote
 * - Success: Shows the fetched quote
 * - No content: Shows placeholder when no quote is available
 * 
 * @param props - The component props
 * @returns A terminal-style quote display with appropriate content based on state
 */
function QuoteDisplay({ quote, isLoading, error }: QuoteDisplayProps) {
  const renderContent = () => {
    if (isLoading) {
      return <QuoteLoading />;
    }

    if (!quote) {
      return error ? <QuoteError error={error} /> : <QuoteNoContent />;
    }

    return <QuoteContent quote={quote} />;
  };

  return (
    <div className="bg-brand-bg/70 backdrop-blur-sm border border-brand-surface rounded-lg shadow-lg max-w-4xl mx-auto font-mono text-lg text-brand-amber">
      {/* Terminal Header */}
      <div className="flex items-center p-3 border-b border-brand-surface">
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <p className="text-center flex-grow text-brand-muted text-sm">
          primatif@session: /bin/wisdom
        </p>
      </div>

      {/* Terminal Body */}
      <div className="p-6 min-h-[150px]">{renderContent()}</div>
    </div>
  );
}

export default QuoteDisplay;